/**
 * @file: EntityCardPickerScreen.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The global entity-card picker as a navigator modal screen — desktop
 * presents it in the shared <Aside> right panel, mobile as a full-screen
 * modal (mirrors OrderFlow). Navigated to via `useEntityCardPicker()`;
 * resolves the caller's promise through the `onResult` param exactly once
 * (on any exit path).
 */

import { Aside, AsideRef } from '@euler/components/Aside';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { EntityCardPickerPanel } from './EntityCardPickerPanel';
import { EntityCardKind, EntityCardPickerResult } from './types';

type Params = {
  kind: EntityCardKind;
  onResult: (result: EntityCardPickerResult | null) => void;
};

export const EntityCardPickerScreen = wrapNavigatorScreen(
  ({ kind, onResult }: Params) => {
    const isMobile = useIsMobileLayout();
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<any>>();
    const asideRef = useRef<AsideRef>(null);
    const resultRef = useRef<EntityCardPickerResult | null>(null);
    const settledRef = useRef(false);

    // Single resolve point — fires on unmount, so every exit path (select,
    // backdrop, back button) settles the caller's promise exactly once.
    useEffect(
      () => () => {
        if (settledRef.current) return;
        settledRef.current = true;
        onResult(resultRef.current);
      },
      [onResult],
    );

    const finish = useCallback(
      (result: EntityCardPickerResult | null) => {
        resultRef.current = result;
        if (isMobile || !asideRef.current) {
          navigation.goBack();
        } else {
          // Animate the aside out; onDismissed pops the screen → unmount
          // settles the promise.
          asideRef.current.dismiss();
        }
      },
      [isMobile, navigation],
    );

    const panel = (
      <EntityCardPickerPanel
        kind={kind}
        onSelect={finish}
        onClose={() => finish(null)}
      />
    );

    if (isMobile) {
      return (
        <View
          style={{
            flex: 1,
            backgroundColor: '#fff',
            paddingTop: insets.top + rpx(4),
          }}
        >
          {panel}
        </View>
      );
    }

    return (
      <Aside
        ref={asideRef}
        dismissible
        disableDefaultNavigationHandler
        onDismissed={() => {
          navigation.goBack();
          return true;
        }}
      >
        {panel}
      </Aside>
    );
  },
  adaptiveWrapperOptions(() => ({ headerShown: false, title: '' })),
);
